import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Header } from '@tanstack/react-table';
import { debounce } from 'lodash';

import { UnixDatePicker } from '../../../../DatePicker';
import { EFilterType } from '../../../types/table';

import styles from './InDateRangeFilter.module.scss';

export interface IInDateRangeFilterProps {
    header: Header<any, unknown>;
    type: EFilterType;
    inline?: boolean;
}

type TInDateRangeFilterValue = [string | undefined, string | undefined];

export const InDateRangeFilter: React.FC<IInDateRangeFilterProps> = ({ header: { column }, inline = false }) => {
    const { t } = useTranslation();
    const filterValue = useMemo(() => {
        return column.getFilterValue() as TInDateRangeFilterValue;
    }, [column.getFilterValue()]);

    const [min, setMin] = useState<string | undefined>(filterValue?.[0]);
    const [max, setMax] = useState<string | undefined>(filterValue?.[1]);

    const handleMinChange = (value: string | undefined) => {
        setMin(value);
        setFilterValueDebounced((old: TInDateRangeFilterValue) => [value, old?.[1]]);
    };
    const handleMaxChange = (value: string | undefined) => {
        setMax(value);
        setFilterValueDebounced((old: TInDateRangeFilterValue) => [old?.[0], value]);
    };

    const setFilterValueDebounced = useCallback(
        debounce((value) => {
            column.setFilterValue(value);
        }, 300),
        [],
    );

    // Replace local value with the value from the column if it was changed
    useEffect(() => {
        if (!filterValue) {
            setMin(undefined);
            setMax(undefined);
            return;
        }
        setMin(filterValue[0]);
        setMax(filterValue[1]);
    }, [filterValue]);

    return (
        <div className={styles.filterWrapper}>
            <UnixDatePicker
                maxValue={max}
                placeholder={t(inline ? 'table.filtering.dateFromShort' : 'table.filtering.dateFrom')}
                size={inline ? 'small' : 'regular'}
                value={min}
                onChange={handleMinChange}
            />
            <UnixDatePicker
                minValue={min}
                placeholder={t(inline ? 'table.filtering.dateToShort' : 'table.filtering.dateTo')}
                size={inline ? 'small' : 'regular'}
                value={max}
                onChange={handleMaxChange}
            />
        </div>
    );
};
